import { useRef, useEffect, useState, ReactNode } from "react";
import { createPortal } from "react-dom";
import { IoCloseOutline } from "react-icons/io5";
import Render from "./Render";

interface ModalComponentProps {
  children: ReactNode;
  onClose: () => void;
  showClose?: boolean;
  containerClass?: string;
  renderHeader?: React.ReactNode;
}

const ModalComponent = (props: ModalComponentProps) => {
  const ref = useRef<Element | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(false);
  const { showClose, children, onClose, containerClass, renderHeader } = props;

  useEffect(() => {
    ref.current = document.querySelector<HTMLElement>("#portal");
    setMounted(true);
    // Disable body scroll
    document.body.style.overflow = "hidden";

    // Function to handle outside clicks
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        event.stopPropagation();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      // Enable body scroll when unmounting
      document.body.style.overflow = "auto";
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return mounted && ref.current
    ? createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-50"></div>
          <div
            ref={modalRef}
            className={`${containerClass} bg-white z-40 flex flex-col overflow-auto p-4 rounded-lg`}
          >
            <Render when={!!showClose}>
              <div className="bg-primary-500 flex p-4">
                <div className="flex-1 text-darkBlue font-medium text-xl">
                  {renderHeader}
                </div>
                <IoCloseOutline
                  size={30}
                  className="cursor-pointer text-secondary-500"
                  onClick={() => {
                    setMounted(false);
                    onClose && onClose();
                  }}
                />
              </div>
            </Render>
            <div className="overflow-scroll h-full no-scrollbar z-50 m-4">
              {children}
            </div>
          </div>
        </div>,
        ref.current
      )
    : null;
};

export default ModalComponent;
